import React, { useState, useRef, useEffect } from "react";
import * as Material from "@mui/material";
import { Button } from "@fragments";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import "../../styles/uploadPhotoStyles.scss";
import { getCroppedImg, resizeAndConvertImage } from "../../utils/resizeImage";

/**
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   title?: string,
 *   aspectRatio: number,
 *   imageMaxWidth?: number,
 *   imageDataUrl: string | null,
 *   onConfirm: (dataUrl: string) => void,
 * }} props
 */
export default function ImageCropModal({
  open,
  onClose,
  title = "Recortar imagem",
  aspectRatio,
  imageMaxWidth = 720,
  imageDataUrl,
  onConfirm,
}) {
  const imageRef = useRef();
  const [crop, setCrop] = useState(null);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!open) {
      setCrop(null);
      setImageSize({ width: 0, height: 0 });
    }
  }, [open]);

  useEffect(() => {
    if (!imageRef.current || !imageSize.width || !imageSize.height) return;
    const { width: dispW, height: dispH } = imageSize;
    let cw;
    let ch;
    const imgRatio = dispW / dispH;
    if (imgRatio > aspectRatio) {
      ch = dispH;
      cw = ch * aspectRatio;
    } else {
      cw = dispW;
      ch = cw / aspectRatio;
    }
    setCrop({
      unit: "px",
      x: (dispW - cw) / 2,
      y: (dispH - ch) / 2,
      width: Math.min(cw, dispW),
      height: Math.min(ch, dispH),
    });
  }, [imageDataUrl, aspectRatio, imageSize]);

  const getDisplayImageSize = (naturalWidth, naturalHeight) => {
    const maxWidth = window.innerWidth * 0.7;
    const maxHeight = window.innerHeight * 0.68;
    let width = naturalWidth;
    let height = naturalHeight;
    const ratio = width / height;
    if (width > maxWidth) {
      width = maxWidth;
      height = width / ratio;
    }
    if (height > maxHeight) {
      height = maxHeight;
      width = height * ratio;
    }
    return {
      width: Math.round(width),
      height: Math.round(height),
    };
  };

  const handleImageLoad = (imgElement) => {
    const naturalWidth = imgElement.naturalWidth;
    const naturalHeight = imgElement.naturalHeight;
    const { width: displayWidth, height: displayHeight } = getDisplayImageSize(
      naturalWidth,
      naturalHeight
    );
    setImageSize({ width: displayWidth, height: displayHeight });
    imageRef.current = imgElement;

    let cropWidth;
    let cropHeight;
    const imageAspectRatio = displayWidth / displayHeight;
    if (imageAspectRatio > aspectRatio) {
      cropHeight = displayHeight;
      cropWidth = cropHeight * aspectRatio;
    } else {
      cropWidth = displayWidth;
      cropHeight = cropWidth / aspectRatio;
    }
    cropWidth = Math.min(cropWidth, displayWidth);
    cropHeight = Math.min(cropHeight, displayHeight);
    setCrop({
      unit: "px",
      x: (displayWidth - cropWidth) / 2,
      y: (displayHeight - cropHeight) / 2,
      width: cropWidth,
      height: cropHeight,
    });
  };

  const onCropChange = (newCrop) => {
    setCrop(newCrop);
  };

  const handleConfirm = async () => {
    if (!imageRef.current || !crop?.width) return;
    let blobUrl;
    try {
      blobUrl = await getCroppedImg(imageRef.current, crop, "crop.jpeg");
      const base64 = await resizeAndConvertImage(blobUrl, imageMaxWidth, aspectRatio);
      onClose();
      // Let the modal exit animation finish before upload / field UI runs.
      window.setTimeout(() => {
        Promise.resolve(onConfirm(base64)).catch((err) => console.error(err));
      }, 280);
    } catch (e) {
      console.error(e);
    } finally {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    }
  };

  return (
    <Material.Modal open={open} onClose={onClose}>
      <Material.Box className="upload-photo-modal">
        <h4 className="resize-photo-title">{title}</h4>

        {imageDataUrl && (
          <div
            className="image-crop-preview mt-2"
            style={{
              maxWidth: "70vw",
              maxHeight: "80vh",
              overflow: "hidden",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <ReactCrop
              aspect={aspectRatio}
              crop={crop}
                onChange={onCropChange}
                ruleOfThirds
              keepSelection
              style={{
                display: "block",
                width: `${imageSize.width}px`,
                height: `${imageSize.height}px`,
                objectFit: "contain",
              }}
            >
              <img
                key={imageDataUrl}
                ref={imageRef}
                src={imageDataUrl}
                alt="Crop"
                style={{
                  display: "block",
                  width: `${imageSize.width}px`,
                  height: `${imageSize.height}px`,
                  objectFit: "contain",
                }}
                onLoad={(e) => handleImageLoad(e.currentTarget)}
              />
            </ReactCrop>
          </div>
        )}

        <Button
          fullWidth
          color="primary"
          className="mt-2 px-2 py-2"
          onClick={handleConfirm}
          disabled={!crop?.width}
        >
          Salvar imagem
        </Button>
      </Material.Box>
    </Material.Modal>
  );
}
